import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { translate } from "@sequentech/ui-core";
import { ESlateCoverageLabel, getSlateCoverageLabel } from "../../services/SlateCoverage";
var Chip = styled("span")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            display: "inline-flex",
            padding: "4px 8px",
            borderRadius: "4px",
            background: theme.palette.lightBackground,
            color: theme.palette.customGrey.dark,
            fontSize: "12px",
            lineHeight: 1.35,
            textAlign: "end",
            overflowWrap: "anywhere"
        },
        _b[theme.breakpoints.down("sm")] = {
            fontSize: "11px",
            padding: "4px 6px",
        },
        _b);
});
var Sentence = styled("p")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            margin: "6px 0 13px",
            fontSize: "14px",
            lineHeight: 1.55,
            color: theme.palette.customGrey.dark,
            overflowWrap: "anywhere"
        },
        _b[theme.breakpoints.down("sm")] = {
            fontSize: "13px",
            margin: "7px 0 0",
        },
        _b);
});
var Count = styled("p")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            margin: "4px 0 12px",
            paddingTop: "12px",
            borderTop: "1px solid ".concat(theme.palette.customGrey.light),
            fontSize: "13px",
            lineHeight: 1.55,
            color: theme.palette.customGrey.dark
        },
        _b[theme.breakpoints.down("sm")] = {
            fontSize: "12px",
            border: 0,
            padding: 0,
            margin: "8px 0 1px",
        },
        _b);
});
/** Whether a slate is full, covers one office only, or is partial. */
export var SlateCoverageChip = function (_a) {
    var _b;
    var coverage = _a.coverage, contests = _a.contests;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var _d = getSlateCoverageLabel(coverage, contests), label = _d.label, contest = _d.contest;
    var text = label === ESlateCoverageLabel.FULL
        ? t("slates.coverage.full")
        : label === ESlateCoverageLabel.SINGLE_CONTEST && contest
            ? t("slates.coverage.singleContest", {
                contest: (_b = translate(contest, "name", i18n.language)) !== null && _b !== void 0 ? _b : "",
            })
            : t("slates.coverage.partial");
    return (_jsx(Chip, { className: "slate-coverage-chip", "data-coverage": coverage.kind, children: text }));
};
/**
 * Which offices a slate has candidates for, and how many candidates and
 * offices that makes.
 */
export var SlateCoverageSummary = function (_a) {
    var slate = _a.slate;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var candidates = slate.contests.reduce(function (total, entry) { return total + entry.candidates.length; }, 0);
    var offices = slate.contests.length;
    var coversEveryOffice = !slate.coverage || slate.coverage.uncovered_contest_ids.length === 0;
    var officeNames = slate.contests
        .map(function (_a) {
        var _b;
        var contest = _a.contest;
        return ((_b = translate(contest, "name", i18n.language)) !== null && _b !== void 0 ? _b : "").toLocaleLowerCase();
    })
        .join(", ");
    return (_jsxs(_Fragment, { children: [slate.coverage ? (_jsx(Sentence, { className: "slate-coverage-sentence", children: coversEveryOffice
                    ? t("slates.coverage.everyOffice")
                    : t("slates.coverage.someOffices", { offices: officeNames }) })) : null, _jsxs(Count, { className: "slate-count", children: [t("slates.coverage.candidates", { count: candidates }), " · ", t("slates.coverage.offices", { count: offices })] })] }));
};
